"use client";

import { Paths } from "@app/routes";
import {
  AppContentFooter,
  type IFormBuilderSchema,
  useFormBuilderTrigger,
  WithPermission,
} from "@atlas/composite";
import {
  Button,
  Card,
  CardContent,
  CardTitle,
  Form,
  StepperWizard,
  useStepperWizardContext,
  useToast,
} from "@atlas/core";
import {
  useCreateFormBuilder,
  useFormBuilder,
  useTranslation,
  useUpdateFormBuilder,
} from "@atlas/sdk";
import type { IHubCatalogType } from "@models";
import { FormGenerator } from "@modules";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useCreateHubCatalogType,
  useHubCatalogType,
  useHubCatalogTypes,
  useUpdateHubCatalogType,
} from "@query";
import { HUB_PERM } from "@shared";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  CatalogTypeDetailsForm,
  type IDetailsFormValues,
} from "./CatalogTypeDetailsForm";

const MINIMAL_FORM_CONFIG = '{"fields":[],"layout":[],"layoutRows":[]}';

const DEFAULT_DETAILS: IDetailsFormValues = {
  name: "",
  label: "",
  description: "",
  parentCatalogTypes: [],
  installUrl: "",
};

function CatalogTypeFooter({
  handleSave,
  handleNext,
  submitForm,
  runPersist,
  submitLabel,
}: {
  handleSave: (step: number) => Promise<boolean>;
  handleNext: (step: number) => Promise<boolean>;
  submitForm: () => { valid: boolean };
  runPersist: (opts: {
    formConfigOverride?: string;
    isSubmitFlow?: boolean;
  }) => Promise<boolean>;
  submitLabel: string;
}) {
  const { step, isLastStep, isSubmitting, goNext } = useStepperWizardContext();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <AppContentFooter>
      <div className="flex w-full items-center justify-end gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate(`/${Paths.CatalogTypeManager}`)}
          disabled={isSubmitting}>
          {t("hub.administer.cancel")}
        </Button>
        {!isLastStep && (
          <Button
            type="button"
            variant="secondary"
            onClick={() => void handleSave(step)}
            disabled={isSubmitting}>
            {t("hub.administer.save")}
          </Button>
        )}
        {isLastStep ? (
          <Button
            type="button"
            onClick={async () => {
              const { valid } = submitForm();
              if (valid) {
                await runPersist({ isSubmitFlow: true });
              }
            }}
            disabled={isSubmitting}>
            {submitLabel}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={async () => {
              const saved = await handleSave(step);
              if (saved) {
                const valid = await handleNext(step);
                if (valid) goNext();
              }
            }}
            disabled={isSubmitting}>
            {t("auth.login.next")}
          </Button>
        )}
      </div>
    </AppContentFooter>
  );
}

export function CreateEditCatalogTypePage({
  typeId: typeIdProp,
}: {
  typeId?: number;
} = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const numericTypeId = typeIdProp != null && typeIdProp > 0 ? typeIdProp : 0;
  const isEdit = numericTypeId > 0;

  const { data: catalogTypes = [] } = useHubCatalogTypes({
    mode: "DATA",
    sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
    startIndex: 0,
    limit: 100,
  });
  const { data: catalogType, isLoading: catalogTypeLoading } =
    useHubCatalogType(numericTypeId, { enabled: isEdit });
  const { data: formData } = useFormBuilder(catalogType?.formId ?? 0, {
    enabled: isEdit && (catalogType?.formId ?? 0) > 0,
  });

  const {
    mutateAsync: createCatalogTypeAsync,
    isPending: isCreateCatalogTypePending,
  } = useCreateHubCatalogType();
  const {
    mutateAsync: updateCatalogTypeAsync,
    isPending: isUpdateCatalogTypePending,
  } = useUpdateHubCatalogType();
  const { mutateAsync: createFormAsync, isPending: isCreateFormPending } =
    useCreateFormBuilder();
  const { mutateAsync: updateFormAsync, isPending: isUpdateFormPending } =
    useUpdateFormBuilder();

  const isSubmitting = useMemo(
    () =>
      isCreateCatalogTypePending ||
      isUpdateCatalogTypePending ||
      isCreateFormPending ||
      isUpdateFormPending,
    [
      isCreateCatalogTypePending,
      isUpdateCatalogTypePending,
      isCreateFormPending,
      isUpdateFormPending,
    ],
  );

  const [formSchema, setFormSchema] = useState<IFormBuilderSchema | null>(null);
  const [generatedFormJson, setGeneratedFormJson] = useState<string>("");
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [formBuilderRef, submitForm] = useFormBuilderTrigger();

  const detailsFormValues = useMemo((): IDetailsFormValues => {
    if (isEdit && catalogType) {
      return {
        name: catalogType.name ?? "",
        label: catalogType.label ?? "",
        description: catalogType.description ?? "",
        parentCatalogTypes: Array.isArray(catalogType.parentCatalogTypes)
          ? catalogType.parentCatalogTypes.map(String)
          : [],
        installUrl: catalogType.installUrl ?? "",
      };
    }
    return DEFAULT_DETAILS;
  }, [isEdit, catalogType]);

  const detailsForm = useForm<IDetailsFormValues>({
    defaultValues: DEFAULT_DETAILS,
    values: detailsFormValues,
    mode: "onChange",
  });

  const parentCatalogTypeOptions = useMemo(
    () =>
      catalogTypes
        .filter(
          (ct: IHubCatalogType) =>
            (!isEdit || (ct.typeId != null && ct.typeId !== numericTypeId)) &&
            (!ct.parentCatalogTypes || ct.parentCatalogTypes.length === 0),
        )
        .map((ct: IHubCatalogType) => ({
          value: String(ct.typeId ?? ""),
          label: ct.label || ct.name,
          description: ct.description || undefined,
        })),
    [catalogTypes, isEdit, numericTypeId],
  );

  const handleSchemaChange = useCallback(
    (
      schema: IFormBuilderSchema | null,
      generatedJson?: string,
      isDirty?: boolean,
    ) => {
      setFormSchema(schema);
      setGeneratedFormJson(
        generatedJson ?? (schema ? JSON.stringify(schema, null, 2) : ""),
      );
      if (isDirty !== undefined) {
        setIsFormDirty(isDirty);
      }
    },
    [],
  );

  const handleNext = useCallback(
    async (currentStep: number) => {
      if (currentStep === 1) {
        const valid = await detailsForm.trigger();
        return valid;
      }
      return true;
    },
    [detailsForm],
  );

  const completedSteps = useMemo(() => {
    const steps: number[] = [];
    if (detailsForm.formState.isValid) steps.push(1);

    const hasFormContent =
      formSchema &&
      ((formSchema.fields && formSchema.fields.length > 0) ||
        (formSchema.layoutRows && formSchema.layoutRows.length > 0));
    if (isEdit || hasFormContent) steps.push(2);

    return steps;
  }, [detailsForm, formSchema, isEdit]);

  const submitLabel = useMemo(
    () =>
      isSubmitting
        ? t("hub.administer.edit_user_submitting")
        : isEdit
          ? t("hub.administer.save")
          : t("hub.catalog_manager.breadcrumb_contribute"),
    [isSubmitting, isEdit, t],
  );

  const steps = useMemo(
    () => [
      {
        title: t("hub.catalog_details.catalog_type"),
        description: t("hub.catalog_type_manager.step_details_desc"),
      },
      {
        title: t("hub.catalog_type_manager.step_form_fields"),
        description: t("hub.catalog_type_manager.step_form_fields_desc"),
      },
    ],
    [t],
  );

  const [isAutosaved, setIsAutosaved] = useState(false);

  const runPersist = useCallback(
    async (args: { formConfigOverride?: string; isSubmitFlow?: boolean }) => {
      const { formConfigOverride, isSubmitFlow = false } = args;
      const details = detailsForm.getValues();
      const parentCatalogTypes = (details.parentCatalogTypes ?? []).map(Number);

      const formConfigJson =
        formConfigOverride ??
        generatedFormJson ??
        (formSchema ? JSON.stringify(formSchema, null, 2) : "");

      if (!formConfigJson.trim()) {
        toast.add({
          title: t("hub.catalog_type_manager.form_required"),
          description: t("hub.catalog_type_manager.build_form_first"),
          data: { type: "error" },
        });
        return false;
      }

      try {
        let savedType: IHubCatalogType;
        if (isEdit && catalogType) {
          await updateFormAsync({
            formId: catalogType.formId,
            formConfig: formConfigJson,
            isActive: true,
          });
          savedType = await updateCatalogTypeAsync({
            ...catalogType,
            name: details.name.trim(),
            label: details.label,
            description: details.description,
            parentCatalogTypes,
            installUrl: details.installUrl,
          });
        } else {
          const newForm = await createFormAsync({
            formConfig: formConfigJson,
            isActive: true,
          });
          savedType = await createCatalogTypeAsync({
            name: details.name.trim(),
            label: details.label,
            description: details.description,
            parentCatalogTypes,
            installUrl: details.installUrl,
            formId: newForm.formId,
          });
        }

        setIsAutosaved(true);

        if (isSubmitFlow) {
          toast.add({
            title: isEdit
              ? t("hub.catalog_type_manager.update_success")
              : t("hub.catalog_type_manager.create_success"),
            data: { type: "success" },
          });
          navigate(`/${Paths.CatalogTypeManager}`);
        } else {
          if (!isEdit && savedType.typeId) {
            navigate(
              `/${Paths.CatalogTypeManager}/${Paths.EditCatalogType}?typeId=${savedType.typeId}`,
            );
          }
        }
        return true;
      } catch (error) {
        const apiError = error as { status?: number; message?: string };
        toast.add({
          title: t("hub.catalog_type_manager.save_error"),
          ...(apiError.status === 409 && apiError.message
            ? { description: apiError.message }
            : {}),
          data: { type: "error" },
        });
        return false;
      }
    },
    [
      detailsForm,
      formSchema,
      generatedFormJson,
      isEdit,
      catalogType,
      createCatalogTypeAsync,
      updateCatalogTypeAsync,
      createFormAsync,
      updateFormAsync,
      navigate,
      toast,
      t,
    ],
  );

  const handleSave = useCallback(
    async (step: number) => {
      if (step === 1) {
        const valid = await detailsForm.trigger();
        if (!valid) return false;
        if (isEdit) {
          if (!formData?.formConfig) {
            toast.add({
              title: t("hub.catalog_type_manager.form_required"),
              description: t("hub.catalog_type_manager.build_form_first"),
              data: { type: "error" },
            });
            return false;
          }
          return await runPersist({ formConfigOverride: formData.formConfig });
        } else {
          return await runPersist({ formConfigOverride: MINIMAL_FORM_CONFIG });
        }
      } else {
        const formConfig =
          generatedFormJson ||
          (formSchema ? JSON.stringify(formSchema, null, 2) : "");
        if (!formConfig.trim()) {
          toast.add({
            title: t("hub.catalog_type_manager.form_required"),
            description: t("hub.catalog_type_manager.build_form_first"),
            data: { type: "error" },
          });
          return false;
        }
        return await runPersist({ formConfigOverride: formConfig });
      }
    },
    [
      detailsForm,
      isEdit,
      formData,
      formSchema,
      generatedFormJson,
      runPersist,
      toast,
      t,
    ],
  );

  return (
    <WithPermission permissions={[HUB_PERM.CATALOG_TYPE_MANAGE]}>
      <StepperWizard
        orientation="vertical"
        completedSteps={completedSteps}
        steps={steps}
        isSubmitting={isSubmitting}
        isAutosaved={isAutosaved}
        onAutosaveComplete={() => setIsAutosaved(false)}
        autosavedLabel={t("hub.create_catalog.autosaved", {
          defaultValue: "Autosaved",
        })}>
        <CatalogTypeFooter
          handleSave={handleSave}
          handleNext={handleNext}
          submitForm={submitForm}
          runPersist={runPersist}
          submitLabel={submitLabel}
        />
        <Form {...detailsForm}>
          <StepperWizard.Step value={1} dirty={detailsForm.formState.isDirty}>
            <CatalogTypeDetailsForm
              isLoading={catalogTypeLoading}
              control={detailsForm.control}
              parentCatalogTypeOptions={parentCatalogTypeOptions}
            />
          </StepperWizard.Step>
          <StepperWizard.Step
            value={2}
            forceMount
            disableScroll
            dirty={isFormDirty}>
            <Card
              variant="content"
              className="border-0 shadow-none flex-1 min-h-0">
              <CardTitle className="mb-1">
                {t("hub.catalog_type_manager.step_form_fields")}
              </CardTitle>
              <div className="text-sm text-muted-foreground mb-4">
                {t("hub.catalog_type_manager.step_form_fields_extended_desc", {
                  defaultValue:
                    "Design the configuration form that users will fill out when installing catalogs of this type. Add fields to collect necessary settings, credentials, or preferences.",
                })}
              </div>
              <CardContent className="p-0 min-h-0">
                <FormGenerator
                  ref={formBuilderRef}
                  formData={
                    isEdit && (catalogType?.formId ?? 0) > 0 ? formData : null
                  }
                  isEdit={isEdit}
                  onSchemaChange={handleSchemaChange}
                  onSubmit={async (_, json) => {
                    await runPersist({
                      formConfigOverride: json,
                      isSubmitFlow: true,
                    });
                  }}
                />
              </CardContent>
            </Card>
          </StepperWizard.Step>
        </Form>
      </StepperWizard>
    </WithPermission>
  );
}
